import React, { memo } from "react";
import { useNavigate } from "react-router-dom";
import { User } from "@supabase/supabase-js";
import { Button } from "@/components/ui/button";
import { Sparkles } from "lucide-react";
import VideoGalleryItem from "@/components/VideoGalleryItem";

interface GalleryVideoItemWrapperProps {
  videoUrl: string;
  videoId: string;
  index: number;
  onToggle: (videoId: string) => void;
  onPlaying: () => void;
  pauseWhenPopupOpen: boolean;
  user?: User | null;
  onLoginRequired?: (redirectPath: string, redirectState?: Record<string, unknown>) => void;
}

const GalleryVideoItemWrapper = memo(({
  videoUrl,
  videoId,
  index,
  onToggle,
  onPlaying,
  pauseWhenPopupOpen,
  user,
  onLoginRequired,
}: GalleryVideoItemWrapperProps) => {
  const navigate = useNavigate();

  const handleClick = (e: React.MouseEvent) => {
    if (window.innerWidth < 768) {
      onToggle(videoId);
    }
  };

  const handleUpscale = (e: React.MouseEvent) => {
    e.stopPropagation();
    
    // Check if user is logged in - if not, show login prompt
    if (!user) {
      if (onLoginRequired) {
        onLoginRequired("/video-upscale", { videoUrl });
      }
      // Don't navigate if user is not logged in
      return;
    }
    
    navigate("/video-upscale", {
      state: {
        videoUrl,
      },
    });
  };

  return (
    <div
      className="gallery-item relative group"
      style={{
        willChange: "opacity",
        transform: "translateZ(0)",
      }}
      data-gallery-video
      data-video-id={videoId}
      onClick={handleClick}
    >
      <VideoGalleryItem
        src={videoUrl}
        alt={`Gallery video ${index + 1}`}
        pauseWhenPopupOpen={pauseWhenPopupOpen}
        onPlaying={onPlaying}
      />
      <div
        className="gallery-overlay absolute inset-0 bg-gradient-to-t from-black/60 via-black/0 to-black/0 transition-opacity duration-300 pointer-events-none opacity-0 md:group-hover:opacity-100"
      />
      <div
        className="gallery-ring absolute inset-0 ring-2 transition-all duration-300 pointer-events-none ring-transparent md:group-hover:ring-gold"
      />

      {/* Upscale 4K 60 FPS Button */}
      <Button
        onClick={handleUpscale}
        className="gallery-button absolute bottom-2 left-2 right-2 transition-opacity duration-300 gradient-premium text-white hover:opacity-90 font-semibold shadow-lg opacity-0 md:group-hover:opacity-100"
        size="sm"
      >
        <Sparkles className="w-4 h-4 mr-2" />
        Upscale 4K 60 FPS
      </Button>
    </div>
  );
}, (prevProps, nextProps) => {
  // Custom comparison - only re-render if actual data props change
  return (
    prevProps.videoUrl === nextProps.videoUrl &&
    prevProps.videoId === nextProps.videoId &&
    prevProps.index === nextProps.index &&
    prevProps.pauseWhenPopupOpen === nextProps.pauseWhenPopupOpen &&
    prevProps.user?.id === nextProps.user?.id
    // Ignoring onToggle, onPlaying, and onLoginRequired as they are stable callbacks
  );
});

GalleryVideoItemWrapper.displayName = "GalleryVideoItemWrapper";

export default GalleryVideoItemWrapper;
